import { Progress } from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import { AdUnit } from '@pubstack/common/src/adunit'
import { BidParamsMapping, BidParamsMappingDimensions, CreateBidParamsMapping, generateMappings, MappingDimensionsConfig } from '@pubstack/common/src/bid-params-mapping'
import { BidderSeat, objectBidParamNames } from '@pubstack/common/src/bidder-seat'
import { BidderCatalog } from '@pubstack/common/src/bidderCatalog'
import { cn } from '@pubstack/ui/utils/cn'
import { DEVICE_TYPES } from '@pubstack/common/src/device-type'
import { MEDIA_TYPES, MediaType } from '@pubstack/common/src/media-types'
import { PbsAdUnit } from '@pubstack/common/src/pbs/adunit'
import { PbsPlacement } from '@pubstack/common/src/pbs/placements'
import { Site } from '@pubstack/common/src/site'
import { byLabel } from '@pubstack/common/src/sort'
import { useQueryClient } from '@tanstack/react-query'
import { FunctionComponent, memo, Ref, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { SITES_QUERY_KEYS, useInfiniteSites, useSite } from '~/api/app/sites.api'
import { BID_PARAMS_MAPPINGS_QUERY_KEYS, useBidParamsMappingsByBidderSeatId, useMappingUpload, useUpsertBidParamsMapping } from '~/api/bid-params-mappings.api'
import { usePlacements } from '~/api/pbs/placements.api'
import { useStacks } from '~/api/stacks.api'
import { Colors } from '~/assets/style/colors'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { useScopePermissions } from '~/auth/useScopePermissions'
import { Alert } from '~/components/Alert'
import Button from '~/components/Button'
import { ConfirmationModal } from '~/components/ConfirmationModal'
import { BaseInputElement } from '~/components/Input'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { LoadMoreFooter } from '~/components/LoadMoreFooter'
import { UnstyledSelect, SelectInput } from '~/components/Select'
import { SelectableOptionsPopover, SelectOptionProp } from '~/components/SelectableOptionsPopover'
import { Spinner } from '~/components/Spinner'
import { Tooltip } from '~/components/Tooltip'
import { Product } from '~/hooks/useProduct'
import { useConfirmNavigation } from '~/utils/useConfirmNavigation'
import { bidParamSpecsOfSeat, canMediaCarryMappedAdUnit, MappableAdUnit, mappingSiteFilters } from './mappingInventory'
import { MappingsSpreadsheet } from './MappingsSpreadsheet'
import { adUnitIdsInStacks, canFilterByStackedAdUnits, isAdUnitUsedInStacks } from './stackedAdUnits'
import { useStackedAdUnitsOnly } from './useStackedAdUnitsOnly'
import { BIDDER_SEATS_QUERY_KEYS } from '~/api/bidder-seats.api'

const UploadInProgress = memo(({ email }: { email: string }) => (
  <div className="mb-3 flex flex-col items-center gap-3">
    <p className="mb-3 text-base leading-[1.625rem] font-bold text-jet">
      A mapping is currently being uploaded by <span className="text-cobalt">{email}</span>
    </p>
    <p className="text-sm leading-[1.375rem] text-jet">It may take a few minutes before the upload is complete. Once completed, you will be able to edit your mapping.</p>
    <Progress value={100} animated w="100%" color={Colors.Ash} />
  </div>
))

const UploadInProgressAlert = ({ email }: { email: string }) => (
  <Alert variant="warning">
    <div className="flex flex-col gap-1 py-2">
      <strong>
        A mapping is currently being uploaded by <span className="text-cobalt">{email}</span>
      </strong>
      <span>It may take a few minutes before the upload is complete. Once completed, you will be able to edit your mapping.</span>
    </div>
  </Alert>
)

// Constants
const ITEMS_PER_PAGE = 25

// Tracking labels
const FILTER_BY_SITE_TRACKING_LABEL = 'pbs-bidder-seat-mapping-media-filter-apply'
const FILTER_BY_AD_UNIT_TRACKING_LABEL = 'pbs-bidder-seat-mapping-ad-unit-filter-apply'
const FILTER_BY_PLACEMENT_ID_TRACKING_LABEL = 'pbs-bidder-seat-mapping-placement-id-filter-apply'
const FILTER_BY_MEDIA_TYPE_TRACKING_LABEL = 'pbs-bidder-seat-mapping-media-type-filter-apply'
const FILTER_BY_ALL_SITES_TRACKING_LABEL = 'pbs-bidder-seat-mapping-all-sites-filter-apply'
const FILTER_BY_MAPPED_SITES_TRACKING_LABEL = 'pbs-bidder-seat-mapping-mapped-sites-filter-apply'
const FILTER_BY_UNMAPPED_SITES_TRACKING_LABEL = 'pbs-bidder-seat-mapping-unmapped-sites-filter-apply'
const LOAD_MORE_SITES_TRACKING_LABEL = 'pbs-bidder-seat-mapping-load-more-sites-apply'
const UPDATE_BID_PARAMS_MAPPINGS_TRACKING_LABEL = 'pbs-bidder-seat-mapping-save'

const MAPPING_STATUS_OPTIONS: SelectOptionProp[] = [
  {
    label: 'All',
    value: 'all',
    trackingLabel: FILTER_BY_ALL_SITES_TRACKING_LABEL,
  },
  {
    label: 'Mapped',
    value: 'mapped',
    trackingLabel: FILTER_BY_MAPPED_SITES_TRACKING_LABEL,
  },
  {
    label: 'Not mapped',
    value: 'unmapped',
    trackingLabel: FILTER_BY_UNMAPPED_SITES_TRACKING_LABEL,
  },
]

/** What a parent driving its own Save button can call. See `hideSaveButton`. */
export type BidderMappingHandle = {
  save: () => Promise<void>
}

type Props = {
  bidderSeat: BidderSeat
  catalogBidder: BidderCatalog
  /** One list, both collections: a cross-inventory seat is mapped over One's ad units and Node's at once. */
  adUnits: MappableAdUnit[]
  platform: 'server' | 'client'
  /**
   * Explicit rather than read from the URL via `useProduct()`: this component is now also opened from the
   * product-neutral `/monetization/inventory/my-bidder-seats` list (a flyout, not a navigation), where
   * `useProduct()` would silently fall back to `adm` and send a Node seat's mutations to the wrong API.
   */
  product: Product
  disabled?: boolean
  onSaveSuccess: () => void
  currentSite?: Site
  onUnsavedMappingsChange?: (hasUnsavedMappings: boolean) => void
  onErroredMappingsChange?: (hasErroredMappings: boolean) => void
  /**
   * Opt-in, for a parent that offers one Save over this mapping *and* something of its own — the seat
   * quick-edit flyout saves the bid adjustment beside it. Hand a `ref` alongside to reach `save()`, and
   * read `onUnsavedMappingsChange`/`onErroredMappingsChange` to know whether calling it would do anything.
   * Both other call sites leave it unset and keep rendering their own button.
   */
  hideSaveButton?: boolean
  /** Grows the mapping table into whatever vertical space its container gives it instead of capping at 450px — see `MappingsSpreadsheet`'s prop of the same name. */
  fillHeight?: boolean
  /**
   * Opens the seat settings, where the custom keys of an `object` bid param are declared. Passed by the
   * seat page, which owns that flyout. Without it, the column header still explains what to do.
   */
  onConfigureObjectParams?: () => void
  ref?: Ref<BidderMappingHandle>
}

export const BidderMapping: FunctionComponent<Props> = ({
  bidderSeat,
  catalogBidder,
  adUnits,
  platform,
  product,
  disabled,
  onSaveSuccess,
  onConfigureObjectParams,
  currentSite,
  onUnsavedMappingsChange,
  onErroredMappingsChange,
  hideSaveButton,
  fillHeight,
  ref,
}) => {
  // Context
  const user = useAuthenticatedUser()
  const { canEdit, disabledReason } = useScopePermissions()
  const scopeId = user.currentScopeId
  const [searchParams] = useSearchParams()
  const siteId = searchParams.get('backToSite')

  // State
  const [siteFilter, setSiteFilter] = useState<Site | undefined>(currentSite)
  const [mediaTypeFilter, setMediaTypeFilter] = useState<string | undefined>(undefined)
  const [siteNameSearch, setSiteNameSearch] = useState<string>('')
  const [adUnitNameSearch, setAdUnitNameSearch] = useState<string>('')
  const [placementIdFilter, setPlacementIdFilter] = useState<string | undefined>(undefined)
  const [placementSearch, setPlacementSearch] = useState('')
  const [isPlacementSuggestionOpen, setIsPlacementSuggestionOpen] = useState(false)
  const [isSiteNamesSuggestionOpen, setIsSiteNamesSuggestionOpen] = useState<boolean>(false)
  const [isAdUnitNamesSuggestionOpen, setIsAdUnitNamesSuggestionOpen] = useState<boolean>(false)
  const [hasUnsavedMappings, setHasUnsavedMappings] = useState<boolean>(false)
  const [hasErroredMappings, setHasErroredMappings] = useState<boolean>(false)
  const [draftMappings, setDraftMappings] = useState<(BidParamsMapping | CreateBidParamsMapping)[]>([])
  const [isMappedFilter, setIsMappedFilter] = useState<boolean | undefined>(undefined)
  // Set from the switch on the seat header, above this spreadsheet; read-only here.
  const [stackedAdUnitsOnly] = useStackedAdUnitsOnly()

  const [debouncedSiteNameSearch] = useDebouncedValue(siteNameSearch, 300)

  const dimensions = bidderSeat.mappingDimensions
  const hasSiteDimension = dimensions.includes('siteId')

  // Which medias this seat covers, and which of the ad units on screen each of them can carry. Both
  // live in `mappingInventory` — the seat header counts the same medias off the same answer.
  const siteScopeFilters = useMemo(() => mappingSiteFilters(bidderSeat, product), [bidderSeat, product])

  // API Hooks
  const { data: defaultSite } = useSite(siteId || '', scopeId)
  const {
    data: sitesData,
    isLoading: isLoadingSites,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteSites({
    scopeId,
    filters: {
      ...siteScopeFilters,
      search: debouncedSiteNameSearch,
      mappedBidderSeatId: bidderSeat.id,
      isMapped: isMappedFilter,
    },
    limit: ITEMS_PER_PAGE,
    sortBy: `mappingCountPerBidderSeat.${bidderSeat.id}`,
    sortOrder: 'descending',
    enabled: !!scopeId && hasSiteDimension,
  })
  const { data: existingMappings, isLoading: isLoadingMapping } = useBidParamsMappingsByBidderSeatId(
    product,
    {
      bidderSeatId: bidderSeat?.id,
      ...(hasSiteDimension ? { siteId: siteFilter?.id } : {}),
    },
    // Site-dimension seats (mappingDimensions containing 'siteId') wait for a site selection;
    // the placement (Spark) view fetches scope-level rows immediately.
    { enabled: hasSiteDimension ? !!siteFilter?.id : true }
  )
  const { activeUpload, activeUploads } = useMappingUpload({
    product,
    bidderSeatId: bidderSeat.id,
    scopeId,
    onSuccess: () => {
      // Refresh mappings when upload completes (even if flyout is closed).
      // Mappings queries are keyed [product, ...BID_PARAMS_MAPPINGS_QUERY_KEYS, ...]; a CSV import can touch many sites, so invalidate the whole product prefix
      queryClient.invalidateQueries({ queryKey: [product, ...BID_PARAMS_MAPPINGS_QUERY_KEYS] })
      queryClient.invalidateQueries({ queryKey: SITES_QUERY_KEYS(bidderSeat.scopeId) })
      // Bidder-seat queries are keyed [product, ...BIDDER_SEATS_QUERY_KEYS, ...]; without the product prefix the seat (and its mapping status) would never refetch
      queryClient.invalidateQueries({ queryKey: [product, ...BIDDER_SEATS_QUERY_KEYS] })
    },
  })
  const { data: placements } = usePlacements()
  const { mutateAsync: upsertMappings, isPending: isMappingsUpsertionPending } = useUpsertBidParamsMapping(product, bidderSeat.id, {
    successToast: (
      <>
        Mapping saved for <strong>{bidderSeat.name}</strong>
      </>
    ),
    errorToast: 'Error while saving the mapping',
  })

  // Hooks
  const modal = useGlobalModal()
  const queryClient = useQueryClient()
  useConfirmNavigation(hasUnsavedMappings)

  // Upload state - block only bidder seats of the same isInternal type
  const otherUpload = activeUploads.find((u) => u.bidderSeatId !== bidderSeat.id && u.isInternal === bidderSeat.isInternal)
  const isDisabled = disabled || !canEdit || !!activeUpload || !!otherUpload

  const hasMediaTypeDimension = dimensions.includes('mediaType')
  const hasPlacementDimension = dimensions.includes('placementId')
  // Shared with the seat header, which shows the switch, and the flyout, which warns on download.
  const isStackedFilterOffered = canFilterByStackedAdUnits(dimensions)
  const hasDeviceDimension = dimensions.includes('device')
  const placementValues = useMemo(() => placements.map((p) => p.id), [placements])

  const sitePages = sitesData?.pages
  const siteOptions = useMemo<SelectOptionProp<Site>[]>(() => {
    const options = (currentSite ? [currentSite] : sitePages?.flatMap((page) => page.data) || []).map((s) => {
      const mappedCount = s.mappingCountPerBidderSeat?.[bidderSeat.id] ?? 0
      const dimensionLabel = hasPlacementDimension ? 'placement' : 'ad unit'
      return {
        value: s,
        label: s.name,
        status: mappedCount === 0 ? 'inactive' : 'active',
        sublabel: mappedCount === 0 ? undefined : `${mappedCount} ${dimensionLabel}${mappedCount > 1 ? 's' : ''} mapped`,
        trackingLabel: FILTER_BY_SITE_TRACKING_LABEL,
      } as SelectOptionProp<Site>
    })

    return options.sort((a, b) => {
      if (a.status !== b.status) {
        return a.status === 'active' ? -1 : 1
      }
      return byLabel(a, b)
    })
  }, [sitePages, bidderSeat, hasPlacementDimension, currentSite])

  /**
   * The ad units the seat can be offered, narrowed by the environments the bidder itself declares.
   *
   * Only bites with no media selected — with one, `canMediaCarryMappedAdUnit` has already answered the
   * same question better, from the media rather than from the bidder. Without it the unfiltered list is
   * the whole scope's inventory, every environment at once, on a seat that may serve only one.
   *
   * An ad unit the seat is already mapped on survives the narrowing whatever its env: the mapping exists
   * and keeps bidding, so hiding its row would stop a publisher reading or removing what is live.
   *
   * A bidder with no `supportedEnvs` narrows nothing. A missing value means the catalog has no data,
   * not that the bidder runs nowhere — the same reading `compatibleSiteEnvs` makes on the catalog page.
   */
  const supported = catalogBidder?.supportedEnvs
  const offerableAdUnitIds = useMemo(() => {
    if (!supported?.length) {
      return undefined
    }
    const alreadyMapped = new Set((existingMappings ?? []).map((mapping) => mapping.dimensions.adUnitId).filter(Boolean))
    return new Set(adUnits.filter((a) => a.product === 'adm' || supported.includes(a.env) || alreadyMapped.has(a.id)).map((a) => a.id))
  }, [adUnits, supported, existingMappings])

  /**
   * The ad units the selected media actually deploys, when the filter asks for them.
   *
   * Scoped to the chosen media, the only reading that holds: the spreadsheet always looks at one media,
   * so "used on a site" can only mean "used on this one". `useStacks` is already gated on `siteId`, and
   * the filter waits for the same thing — with no media picked, an empty stack list would otherwise read
   * as "this media deploys nothing" and blank both the spreadsheet and the ad unit search.
   */
  const { data: siteStacks } = useStacks({ siteId: siteFilter?.id, type: 'adm', status: ['to-deploy', 'live'] })
  const stackedAdUnitIds = useMemo(() => adUnitIdsInStacks(siteStacks), [siteStacks])
  /**
   * `siteFilter` because the ad unit ids come from that media's stacks: with no media picked `useStacks`
   * is disabled, and an empty set would read as "nothing is deployed" and blank the list.
   * `isStackedFilterOffered` because the preference is stored per user and follows them onto seats the
   * filter cannot answer for — the same condition the flyout uses to decide whether to show the switch.
   */
  const isStackedFilterActive = stackedAdUnitsOnly && !!siteFilter && isStackedFilterOffered

  const filteredAdUnits = useMemo(
    () =>
      (siteFilter ? adUnits.filter((a) => canMediaCarryMappedAdUnit(bidderSeat, siteFilter, a)) : adUnits.filter((a) => !offerableAdUnitIds || offerableAdUnitIds.has(a.id)))
        .filter((e) => e.name?.toLowerCase().includes(adUnitNameSearch.toLowerCase()))
        // ADM ad units with header bidding disabled can't be mapped, so hide them from the spreadsheet (PbsAdUnit has no such flag).
        .filter((e) => !('headerBiddingEnabled' in e) || e.headerBiddingEnabled)
        // Deliberately applied to the already-mapped ad units too, unlike `offerableAdUnitIds` above: an
        // ad unit mapped on a media it is no longer deployed on is exactly what the filter is asked to
        // clear away, and the row comes back the moment the filter is turned off.
        .filter((e) => !isStackedFilterActive || isAdUnitUsedInStacks(e, stackedAdUnitIds)),
    [adUnits, siteFilter, adUnitNameSearch, bidderSeat, offerableAdUnitIds, isStackedFilterActive, stackedAdUnitIds]
  )

  const filteredAdUnitIds = useMemo<string[]>(() => filteredAdUnits.map((e) => e.id), [filteredAdUnits])

  const filteredAdUnitOptions = useMemo<SelectOptionProp<PbsAdUnit>[]>(() => {
    const options = filteredAdUnits.map((e) => ({ value: e, label: e.name, trackingLabel: FILTER_BY_AD_UNIT_TRACKING_LABEL }) as SelectOptionProp<PbsAdUnit>)
    return options.sort(byLabel)
  }, [filteredAdUnits])
  const mediaTypeOptions = useMemo<SelectOptionProp<MediaType | undefined>[]>(
    () => [
      {
        label: 'All',
        value: undefined,
        trackingLabel: FILTER_BY_MEDIA_TYPE_TRACKING_LABEL,
      },
      ...(catalogBidder.supportedMediaTypes || Array.from(MEDIA_TYPES)).map(
        (e) => ({ value: e, label: e as string, trackingLabel: FILTER_BY_MEDIA_TYPE_TRACKING_LABEL }) as SelectOptionProp<MediaType>
      ),
    ],
    [catalogBidder.supportedMediaTypes]
  )

  /**
   * The placements matching what has been typed, as suggestions rather than as a dropdown.
   *
   * A scope's placements are not a short fixed list the way media types are — they run to hundreds, and
   * the two screens that filter by one are the Spark tabs, where finding a placement means knowing its
   * name and not scrolling to it. So it takes the shape the ad unit filter beside it already has: a
   * search box that narrows as you type. Nothing typed means no filter, which is what the "All" entry
   * of the old dropdown was for.
   */
  const filteredPlacementOptions = useMemo<SelectOptionProp<PbsPlacement>[]>(() => {
    const search = placementSearch.trim().toLocaleLowerCase()
    return placements
      .filter((placement) => !search || placement.name.toLocaleLowerCase().includes(search))
      .map((placement) => ({ value: placement, label: placement.name, trackingLabel: FILTER_BY_PLACEMENT_ID_TRACKING_LABEL }) as SelectOptionProp<PbsPlacement>)
  }, [placements, placementSearch])

  // Site-level dimensions require a site to be selected
  const mappingDimensionsConfig: MappingDimensionsConfig | undefined =
    hasSiteDimension && !siteFilter
      ? undefined
      : {
          // Include siteId dimension only when the bidder seat uses it
          ...(hasSiteDimension && siteFilter
            ? {
                siteId: {
                  values: [siteFilter.id],
                  labels: { [siteFilter.id]: siteFilter.name || '' },
                },
              }
            : {}),
          // Use placementId or adUnitId depending on the configuration
          ...(hasPlacementDimension
            ? {
                placementId: {
                  values: placementValues,
                  labels: Object.fromEntries(placements.map((p) => [p.id, p.name])),
                },
              }
            : {
                adUnitId: {
                  values: filteredAdUnits.map((a) => a.id),
                  labels: Object.fromEntries(filteredAdUnits.map((a) => [a.id, a.name])),
                },
              }),
          ...(hasMediaTypeDimension
            ? {
                mediaType: {
                  values: (catalogBidder?.supportedMediaTypes || Array.from(MEDIA_TYPES)).map((e) => e as string),
                  labels: Object.fromEntries((catalogBidder?.supportedMediaTypes || Array.from(MEDIA_TYPES)).map((e) => [e, e])),
                },
              }
            : {}),
          ...(hasDeviceDimension
            ? {
                device: {
                  values: [...DEVICE_TYPES],
                  labels: Object.fromEntries([...DEVICE_TYPES].map((e) => [e, e])),
                },
              }
            : {}),
        }

  const bidParamSpecs = useMemo(() => bidParamSpecsOfSeat(bidderSeat, catalogBidder, platform), [bidderSeat, catalogBidder, platform])

  // Namespaces this bidder offers (OneTag's `ext`) that the seat has not filled in yet. They give no
  // column, so without a hint nothing on the screen says the bidder takes custom keys at all.
  const unconfiguredObjectParams = useMemo(
    () => objectBidParamNames(catalogBidder.bidParams).filter((name) => !bidderSeat.extraObjectBidParams?.[name]?.length),
    [catalogBidder.bidParams, bidderSeat.extraObjectBidParams]
  )

  /**
   * A namespace with no keys yet still gets a column, empty and read-only.
   *
   * `expandObjectBidParams` drops it, which is right since it holds no value. But then nothing on this
   * screen would say the bidder takes custom keys. The column says it, and its header opens the settings.
   */
  const spreadsheetBidParamSpecs = useMemo(
    () => [...bidParamSpecs, ...unconfiguredObjectParams.map((name) => ({ name, required: false, type: 'object' as const }))],
    [bidParamSpecs, unconfiguredObjectParams]
  )

  const allMappings = useMemo(() => {
    if (!mappingDimensionsConfig) return []

    return generateMappings({
      existingMappings,
      siteAdUnitCouplesToExclude: new Set(),
      mappingDimensionsConfig,
      bidderSeatId: bidderSeat.id,
      adUnits: product === 'adm' ? (adUnits as AdUnit[]) : [],
    })
  }, [existingMappings, mappingDimensionsConfig, bidderSeat.id, product, adUnits])

  const filteredMappings = useMemo(
    () =>
      allMappings.filter((mapping) => {
        // Media type filter
        if (hasMediaTypeDimension && mediaTypeFilter && mapping.dimensions.mediaType !== mediaTypeFilter) {
          return false
        }

        // Dimension filter
        if (hasPlacementDimension) {
          return !placementIdFilter || mapping.dimensions.placementId === placementIdFilter
        }

        return mapping.dimensions.adUnitId && filteredAdUnitIds.includes(mapping.dimensions.adUnitId)
      }),
    [allMappings, filteredAdUnitIds, hasMediaTypeDimension, mediaTypeFilter, hasPlacementDimension, placementIdFilter]
  )

  const hiddenColumns = useMemo<(keyof BidParamsMappingDimensions)[]>(() => {
    const hiddenColumns: (keyof BidParamsMappingDimensions)[] = ['siteId']
    if (hasMediaTypeDimension && mediaTypeFilter) hiddenColumns.push('mediaType')
    if (hasPlacementDimension && placementIdFilter) hiddenColumns.push('placementId')
    return hiddenColumns
  }, [hasMediaTypeDimension, mediaTypeFilter, hasPlacementDimension, placementIdFilter])

  /** Built once and placed by whichever branch of the filter bar is live, so the two cannot drift. */
  const placementFilter = (
    <SelectableOptionsPopover
      open={isPlacementSuggestionOpen}
      options={filteredPlacementOptions}
      setOpen={setIsPlacementSuggestionOpen}
      trigger={
        <BaseInputElement
          iconLeft="search"
          name="Placement"
          label="Placement"
          labelIsPlaceholder
          value={placementSearch}
          onChange={(event) => {
            setPlacementSearch(event.target.value)
            // Typing past a chosen placement is a new search, not a narrower one — the rows go back to
            // the full set until something is picked again.
            setPlacementIdFilter(undefined)
          }}
          onClear={() => {
            setPlacementSearch('')
            setPlacementIdFilter(undefined)
          }}
        />
      }
      onChange={(option) => {
        const placement = option.value as PbsPlacement
        setPlacementSearch(placement.name)
        setPlacementIdFilter(placement.id)
      }}
      maxWidthPopoverSameAsTrigger={true}
    />
  )

  const handleSiteClear = useCallback(() => {
    if (hasUnsavedMappings) {
      modal.open(ConfirmationModal, {
        title: 'Unsaved changes',
        message: 'You have unsaved changes. Are you sure you want to continue?',
        trackingLabel: 'common-confirm-navigation',
        onCancel: () => {
          modal.close()
        },
        onConfirm: () => {
          modal.close()
          setSiteFilter(undefined)
        },
      })
    } else {
      setSiteFilter(undefined)
    }
  }, [hasUnsavedMappings, modal])

  const handleSiteChange = useCallback(
    (e: SelectOptionProp) => {
      if (hasUnsavedMappings) {
        modal.open(ConfirmationModal, {
          title: 'Unsaved changes',
          message: 'You have unsaved changes. Are you sure you want to continue?',
          trackingLabel: 'common-confirm-navigation',
          onCancel: () => {
            modal.close()
          },
          onConfirm: () => {
            modal.close()
            const site = e.value as Site
            setSiteFilter(site)
            setSiteNameSearch('')
          },
        })
      } else {
        const site = e.value as Site
        setSiteFilter(site)
        setSiteNameSearch('')
      }
    },
    [hasUnsavedMappings, modal]
  )

  const handleSave = useCallback(async () => {
    if (hasErroredMappings || draftMappings.length === 0) return

    await upsertMappings(draftMappings)

    onSaveSuccess()
    queryClient.invalidateQueries({ queryKey: [...SITES_QUERY_KEYS(bidderSeat.scopeId), 'infinite'] })
  }, [upsertMappings, draftMappings, onSaveSuccess, hasErroredMappings, queryClient, bidderSeat.scopeId])

  const handleStatusFilterChange = (option: SelectOptionProp) => {
    const val = option.value as string
    if (val === 'all') setIsMappedFilter(undefined)
    else if (val === 'mapped') setIsMappedFilter(true)
    else setIsMappedFilter(false)
  }

  const handleLoadMore = useCallback(() => {
    if (isFetchingNextPage) return
    fetchNextPage()
  }, [isFetchingNextPage, fetchNextPage])

  const handleDraftMappingsChange = useCallback(
    (e: (BidParamsMapping | CreateBidParamsMapping)[]) => {
      setDraftMappings(e)
      setHasUnsavedMappings(e.length > 0)
      onUnsavedMappingsChange?.(e.length > 0)
    },
    [onUnsavedMappingsChange]
  )

  const handleErroredMappingsChange = useCallback(
    (e: boolean) => {
      setHasErroredMappings(e)
      onErroredMappingsChange?.(e)
    },
    [onErroredMappingsChange]
  )

  // `handleSave` no-ops on an empty draft, so a parent Save may call it unconditionally.
  useImperativeHandle(ref, () => ({ save: handleSave }), [handleSave])

  useEffect(() => {
    if (siteId && defaultSite && !siteFilter && !currentSite) {
      // Initialises the site filter from the default site.
      // oxlint-disable-next-line react/set-state-in-effect
      setSiteFilter(defaultSite)
    }
  }, [siteId, defaultSite, siteFilter])

  // Clear the parent's unsaved-mappings flag when this view unmounts, so the flag never gets stuck.
  useEffect(() => () => onUnsavedMappingsChange?.(false), [onUnsavedMappingsChange])
  useEffect(() => () => onErroredMappingsChange?.(false), [onErroredMappingsChange])

  // Show upload in progress screen if current bidder seat has an active upload
  if (activeUpload) {
    return <UploadInProgress email={activeUpload.updatedBy} />
  }

  return (
    <div className={cn('flex flex-col gap-2', fillHeight && 'min-h-0 flex-1')}>
      {/* Alert if another bidder seat of same type has upload in progress */}
      {otherUpload && <UploadInProgressAlert email={otherUpload.updatedBy} />}

      {/* Mapping information from the bidder */}
      {catalogBidder.mappingInfo && (
        <Alert variant="primary" iconName="info">
          {catalogBidder.mappingInfo}
        </Alert>
      )}

      {/* Mapping warning from the bidder */}
      {catalogBidder.supportedEnvs?.every((env) => env === 'inapp') && (
        <Alert variant="warning" iconName="warning">
          {catalogBidder.displayName} is compatible only for in-app media. Be sure to apply your mapping only to in-app media before saving it.
        </Alert>
      )}

      {hasSiteDimension && <p>Select a media below to edit its mapping.</p>}

      <div className="my-4 flex min-w-0 items-center gap-5 [&>*]:w-[300px]">
        {hasSiteDimension && (
          <SelectableOptionsPopover
            open={isSiteNamesSuggestionOpen}
            options={siteOptions}
            optionsLoading={isLoadingSites}
            setOpen={setIsSiteNamesSuggestionOpen}
            footerOption={hasNextPage && <LoadMoreFooter loading={isFetchingNextPage} onLoadMore={handleLoadMore} trackingLabel={LOAD_MORE_SITES_TRACKING_LABEL} />}
            filterSection={
              <div className="flex flex-col gap-3 p-3 [&>*]:w-full">
                <BaseInputElement
                  variant="small"
                  iconLeft="search"
                  labelIsPlaceholder
                  value={siteNameSearch}
                  onChange={(e) => setSiteNameSearch(e.target.value)}
                  onClear={() => setSiteNameSearch('')}
                />
                <UnstyledSelect
                  variant="small"
                  label=""
                  value={isMappedFilter === undefined ? 'all' : isMappedFilter ? 'mapped' : 'unmapped'}
                  onChange={handleStatusFilterChange}
                  options={MAPPING_STATUS_OPTIONS}
                />
              </div>
            }
            trigger={
              <SelectInput labelIsPlaceholder isOpen={isSiteNamesSuggestionOpen} search={false} label="Media" iconRight="chevron_down" value={siteFilter?.name || ''} onClear={handleSiteClear} />
            }
            onChange={handleSiteChange}
            maxWidthPopoverSameAsTrigger={true}
          />
        )}
        {hasSiteDimension &&
          (hasPlacementDimension ? (
            placementFilter
          ) : (
            <SelectableOptionsPopover
              open={isAdUnitNamesSuggestionOpen}
              options={filteredAdUnitOptions}
              setOpen={setIsAdUnitNamesSuggestionOpen}
              trigger={
                <BaseInputElement
                  iconLeft="search"
                  name="Ad unit"
                  label="Ad unit"
                  labelIsPlaceholder
                  value={adUnitNameSearch}
                  onChange={(e) => setAdUnitNameSearch(e.target.value)}
                  onClear={() => setAdUnitNameSearch('')}
                />
              }
              onChange={(e) => {
                const adUnit = e.value as PbsAdUnit
                setAdUnitNameSearch(adUnit.name)
              }}
              maxWidthPopoverSameAsTrigger={true}
            />
          ))}
        {!hasSiteDimension && hasPlacementDimension && placementFilter}
        {hasMediaTypeDimension && <UnstyledSelect label="Media type" value={mediaTypeFilter} onChange={(e) => setMediaTypeFilter(e.value as string)} options={mediaTypeOptions} />}
      </div>

      {isLoadingMapping ? (
        <Spinner className="m-auto" />
      ) : (
        <>
          <MappingsSpreadsheet
            bidderSeat={bidderSeat}
            mappingDimensionsConfig={mappingDimensionsConfig}
            hiddenColumns={hiddenColumns}
            bidParamSpecs={spreadsheetBidParamSpecs}
            readOnlyParamNames={unconfiguredObjectParams}
            onConfigureParam={onConfigureObjectParams}
            mappings={filteredMappings}
            readOnly={isDisabled}
            onDraftMappingsChange={handleDraftMappingsChange}
            onErroredMappingsChange={handleErroredMappingsChange}
            fillHeight={fillHeight}
          />

          {!hideSaveButton && (
            <div className="flex justify-end">
              <Tooltip title={disabledReason} disabled={canEdit}>
                <Button
                  disabled={isMappingsUpsertionPending || draftMappings.length === 0 || hasErroredMappings || isDisabled}
                  iconName={isMappingsUpsertionPending ? 'pubstack' : undefined}
                  iconSpin={isMappingsUpsertionPending}
                  onClick={handleSave}
                  trackingLabel={UPDATE_BID_PARAMS_MAPPINGS_TRACKING_LABEL}
                >
                  {isMappingsUpsertionPending ? 'Saving mapping...' : 'Save mapping'}
                </Button>
              </Tooltip>
            </div>
          )}
        </>
      )}
    </div>
  )
}
